import { AgChartOptions } from "ag-charts-community";
import { Maximize2 } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import AnalyticsChartFrame, { type AnalyticsChartFrameHandle } from "./AnalyticsChartFrame";
import AnalyticsChartToolbarButton from "./AnalyticsChartToolbarButton";
import {
  ANALYTICS_CHART_LOADING_LABEL,
  ANALYTICS_GRAPH_CHART_CARD_CLASS,
  ANALYTICS_GRAPH_FULLSCREEN_CHART_HEIGHT_CLASS,
  ANALYTICS_GRAPH_FULLSCREEN_DIALOG_CLASS,
  FULLSCREEN_RESET_KEY_SUFFIX,
} from "./constants";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface AnalyticsGraphChartCardShellProps {
  title: string;
  chartOptions: AgChartOptions;
  resetKey: string;
  isLoading: boolean;
  isEmpty: boolean;
  toolbarActions?: ReactNode;
  footer?: ReactNode;
  className?: string;
  chartFrameRef?: React.RefObject<AnalyticsChartFrameHandle>;
  fullscreenAriaLabel?: string;
}

const AnalyticsGraphChartCardShell = ({
  title,
  chartOptions,
  resetKey,
  isLoading,
  isEmpty,
  toolbarActions,
  footer,
  className,
  chartFrameRef: externalChartFrameRef,
  fullscreenAriaLabel = "Open chart fullscreen",
}: AnalyticsGraphChartCardShellProps) => {
  const internalChartFrameRef = useRef<AnalyticsChartFrameHandle>(null);
  const chartFrameRef = externalChartFrameRef ?? internalChartFrameRef;
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const sharedChartFrameProps = {
    options: chartOptions,
    isLoading,
    isEmpty,
    loadingLabel: ANALYTICS_CHART_LOADING_LABEL,
  };

  return (
    <>
      <div className={cn(ANALYTICS_GRAPH_CHART_CARD_CLASS, className)}>
        <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
          <h4 className="min-w-0 text-base font-semibold text-gray-900">{title}</h4>
          <div className="flex shrink-0 items-center gap-0.5">
            <AnalyticsChartToolbarButton
              ariaLabel={fullscreenAriaLabel}
              icon={Maximize2}
              onClick={() => setIsFullscreenOpen(true)}
              tooltip="Open fullscreen"
            />
            {toolbarActions}
          </div>
        </div>
        {!isFullscreenOpen ? (
          <AnalyticsChartFrame ref={chartFrameRef} resetKey={resetKey} {...sharedChartFrameProps} />
        ) : null}
        {footer}
      </div>

      <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
        {isFullscreenOpen ? (
          <DialogContent className={ANALYTICS_GRAPH_FULLSCREEN_DIALOG_CLASS}>
            <DialogHeader className="shrink-0 pr-8 text-left">
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            <AnalyticsChartFrame
              resetKey={`${resetKey}${FULLSCREEN_RESET_KEY_SUFFIX}`}
              className={ANALYTICS_GRAPH_FULLSCREEN_CHART_HEIGHT_CLASS}
              {...sharedChartFrameProps}
            />
            {footer}
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
};

export default AnalyticsGraphChartCardShell;
